// src/lib/constants/appFactory.ts

// ── Build statuses (factory app_build2 machine) ──
export const FACTORY_BUILD_STATUSES = [
  'queued',
  'building',
  'built',
  'preflight_passed',
  'awaiting_customer',
  'failed',
  'superseded',
] as const;
export type FactoryBuildStatus = typeof FACTORY_BUILD_STATUSES[number];

// ── Submission states (factory app_submission machine) ──
export const FACTORY_SUBMISSION_STATES = [
  'preparing',
  'awaiting_customer',
  'submitting',
  'in_review',
  'approved',
  'rejected',
  'live',
  'superseded',
] as const;
export type FactorySubmissionState = typeof FACTORY_SUBMISSION_STATES[number];

// ── Display labels ──
export const FACTORY_BUILD_STATUS_LABELS: Record<string, string> = {
  queued: 'Queued',
  building: 'Building',
  built: 'Built',
  preflight_passed: 'Checks Passed',
  awaiting_customer: 'Ready for store',
  failed: 'Failed',
  superseded: 'Superseded',
};

export const FACTORY_SUBMISSION_STATE_LABELS: Record<string, string> = {
  preparing: 'Preparing',
  awaiting_customer: 'Ready to submit',
  submitting: 'Submitting',
  in_review: 'In Review',
  approved: 'Approved',
  rejected: 'Rejected',
  live: 'Live',
  superseded: 'Superseded',
};

// ── Badge colors (Mantine palette names) ──
export const FACTORY_BUILD_STATUS_COLORS: Record<string, string> = {
  queued: 'gray',
  building: 'blue',
  built: 'cyan',
  preflight_passed: 'teal',
  awaiting_customer: 'orange',
  failed: 'red',
  superseded: 'gray',
};

export const FACTORY_SUBMISSION_STATE_COLORS: Record<string, string> = {
  preparing: 'gray',
  awaiting_customer: 'orange',
  submitting: 'blue',
  in_review: 'indigo',
  approved: 'teal',
  rejected: 'red',
  live: 'green',
  superseded: 'gray',
};

/**
 * One line of state-specific guidance under a submission's badge — what the
 * state MEANS for the person reading the card (B1: the generic "waiting on
 * your action" badge told nobody what to do).
 */
export const FACTORY_SUBMISSION_STATE_GUIDANCE: Record<string, string> = {
  preparing: 'The build is being checked and packaged. Nothing to do yet.',
  awaiting_customer: 'The package and guide are ready for the store portal.',
  submitting: 'The package is being entered in the store portal.',
  in_review: 'The store is reviewing the channel. Reviews usually take a few business days.',
  approved: 'The store approved the channel. It goes live on the scheduled publish date.',
  rejected: 'The store rejected this submission. A new build starts a fresh submission.',
  live: 'This version is live in the store.',
  superseded: 'A newer submission replaced this one.',
};

/**
 * Store-specific wording over the guidance above (which speaks Roku's "channel"
 * and "store portal"); a state a store does not list falls back to it.
 */
export const FACTORY_STORE_STATE_GUIDANCE: Partial<Record<string, Record<string, string>>> = {
  amazon: {
    preparing: 'The update is being built and checked. Nothing to do yet.',
    awaiting_customer: 'The update is built, checked and ready for the Amazon Appstore.',
    submitting: 'The update is being submitted to the Amazon Appstore.',
    in_review: 'Amazon is reviewing the update. Reviews usually take a few days.',
    approved: 'Amazon approved the update. It reaches devices shortly after approval.',
    rejected: 'Amazon rejected this update. A new build starts a fresh submission.',
  },
};

// ── Store accounts (4.2 delegated store accounts) ──
export const FACTORY_STORE_ACCOUNT_STATES = [
  'pending',
  'invited',
  'active',
  'action_needed',
  'revoked',
] as const;
export type FactoryStoreAccountState = typeof FACTORY_STORE_ACCOUNT_STATES[number];

export const FACTORY_STORE_ACCOUNT_STATE_LABELS: Record<string, string> = {
  pending: 'Not set up',
  invited: 'Invite sent',
  active: 'Active',
  action_needed: 'Action needed',
  revoked: 'Revoked',
};

export const FACTORY_STORE_ACCOUNT_STATE_COLORS: Record<string, string> = {
  pending: 'gray',
  invited: 'blue',
  active: 'green',
  action_needed: 'orange',
  revoked: 'red',
};

export const FACTORY_STORE_ACCOUNT_HOLDER_KINDS = ['customer', 'reseller', 'streamhoster'] as const;
export type FactoryStoreAccountHolderKind = typeof FACTORY_STORE_ACCOUNT_HOLDER_KINDS[number];

export const FACTORY_STORE_ACCOUNT_SUBMITTERS = ['operator', 'holder'] as const;
export type FactoryStoreAccountSubmitter = typeof FACTORY_STORE_ACCOUNT_SUBMITTERS[number];

export const FACTORY_STORE_ACCOUNT_CEREMONIES = ['operator', 'reseller', 'customer'] as const;

/** Stores the App Center onboards today (Apple/Google come with their shells). */
export const FACTORY_STORES = ['roku', 'amazon'] as const;
export type FactoryStoreKey = typeof FACTORY_STORES[number];

export interface FactoryStoreInfo {
  key: FactoryStoreKey;
  label: string;
  /** The store's developer portal. */
  portalUrl: string;
  /** Where in the portal the user is added (one sentence, verified in the demo walk). */
  inviteLocation: string;
  /** The download button on a submission card: what the store's binary is. */
  packageLabel: string;
  /**
   * The build system itself records "Ready to submit" the moment a build passes
   * its checks (Amazon). With a delegated account that state is never a task for
   * the customer: the operator submits the update.
   */
  readyIsAutomatic: boolean;
}

export const FACTORY_STORE_INFO: Record<FactoryStoreKey, FactoryStoreInfo> = {
  roku: {
    key: 'roku',
    label: 'Roku',
    portalUrl: 'https://developer.roku.com',
    inviteLocation: 'Manage My Account → Users → Add User',
    packageLabel: 'Download package (.pkg)',
    readyIsAutomatic: false,
  },
  amazon: {
    key: 'amazon',
    label: 'Amazon Appstore',
    portalUrl: 'https://developer.amazon.com',
    inviteLocation: 'My Settings → User Permissions → Add User',
    packageLabel: 'Download app file (.apk)',
    readyIsAutomatic: true,
  },
};

/**
 * A store whose FIRST version cannot be submitted for the customer by the build
 * system (Amazon: its submission service only updates an app that already
 * exists in the console). Shown on the platform's Status tab until the store's
 * app id is recorded; stores without an entry have no such step.
 */
export interface FactoryStoreFirstSubmission {
  title: string;
  facts: string[];
  /** Who does it, per the account's submitter; `{operator}` = the operator's name. */
  byOperator: string;
  byHolder: string;
  noAccount: string;
  after: string;
}

export const FACTORY_STORE_FIRST_SUBMISSION: Partial<Record<FactoryStoreKey, FactoryStoreFirstSubmission>> = {
  amazon: {
    title: 'The first Amazon submission is done in the Amazon developer console',
    facts: [
      'Amazon only accepts automatic submissions for an app that already exists in its developer console, so the first version is entered there by hand.',
      'That first version sets what the console keeps for every later one: availability and pricing, the content rating, the store listing and images, and the first app file.',
    ],
    byOperator:
      '{operator} enters the first version for you through your Amazon developer account once the first build passes its checks — nothing to do.',
    byHolder:
      'You enter the first version in the Amazon developer console with the app file from the submission card below, then record each step here.',
    noAccount:
      'Set up the Amazon developer account first — the Store readiness card below opens that step.',
    after:
      'Once Amazon approves it, the Amazon App ID is recorded here and every later update is submitted without the console.',
  },
};

/** The submission edges a HOLDER (the customer or their reseller) may record; the operator records the rest. */
export const FACTORY_HOLDER_SUBMISSION_FROM_STATES = [
  'awaiting_customer',
  'submitting',
  'in_review',
  'approved',
] as const;

// ── Query keys ──
export const APP_FACTORY_QUERY_KEYS = {
  all: ['appFactory'] as const,
  storeAccounts: (cId: number) => [...APP_FACTORY_QUERY_KEYS.all, 'storeAccounts', cId] as const,
  releases: (cId: number) => [...APP_FACTORY_QUERY_KEYS.all, 'releases', cId] as const,
  tasks: (cId: number) => [...APP_FACTORY_QUERY_KEYS.all, 'tasks', cId] as const,
  builds: (cId: number, appCfgId: number) =>
    [...APP_FACTORY_QUERY_KEYS.all, 'builds', cId, appCfgId] as const,
  submissions: (cId: number, appCfgId: number) =>
    [...APP_FACTORY_QUERY_KEYS.all, 'submissions', cId, appCfgId] as const,
  devices: (cId: number) => [...APP_FACTORY_QUERY_KEYS.all, 'devices', cId] as const,
  activeSubmissions: (cId: number) =>
    [...APP_FACTORY_QUERY_KEYS.all, 'activeSubmissions', cId] as const,
  campaigns: (cId: number) => [...APP_FACTORY_QUERY_KEYS.all, 'campaigns', cId] as const,
  campaignTargets: (cId: number, campaignId: string) =>
    [...APP_FACTORY_QUERY_KEYS.all, 'campaignTargets', cId, campaignId] as const,
  assetSets: (cId: number, appCfgId: number) =>
    [...APP_FACTORY_QUERY_KEYS.all, 'assetSets', cId, appCfgId] as const,
} as const;

// ── Asset-set states + labels/colors ──
export const ASSET_SET_STATE_LABELS: Record<string, string> = {
  generated: 'Awaiting Approval',
  approved: 'Approved',
  rejected: 'Rejected',
};

export const ASSET_SET_STATE_COLORS: Record<string, string> = {
  generated: 'orange',
  approved: 'green',
  rejected: 'gray',
};

// ── Deliverable slots (mirrors the factory spec table, assets/specs.go) ──
//
// The manual upload arm builds ONE asset set from these slots; the factory
// re-validates mechanically (ADR-0012 gates) and approval publishes. A
// partial set is legal — it overlays onto the current set.

export interface AssetDeliverableSlot {
  /** Exact spec basename — the manual-mode files key. */
  name: string;
  label: string;
  w: number;
  h: number;
  /** Required encoding, enforced server-side ('png' needs alpha for oh_logo). */
  format: 'png' | 'jpg';
  group: string;
}

export const ASSET_DELIVERABLE_GROUPS = {
  PACKAGE: 'Channel package',
  EXPERIENCE: 'In-app experience',
  LISTING: 'Store listing',
} as const;

export const APP_ROKU_DELIVERABLE_SLOTS: AssetDeliverableSlot[] = [
  { name: 'icon_fhd.png', label: 'Channel Icon (FHD)', w: 540, h: 405, format: 'png', group: ASSET_DELIVERABLE_GROUPS.PACKAGE },
  { name: 'icon_hd.png', label: 'Channel Icon (HD)', w: 290, h: 218, format: 'png', group: ASSET_DELIVERABLE_GROUPS.PACKAGE },
  { name: 'icon_sd.png', label: 'Channel Icon (SD)', w: 246, h: 140, format: 'png', group: ASSET_DELIVERABLE_GROUPS.PACKAGE },
  { name: 'splash_fhd.jpg', label: 'Splash Screen (FHD)', w: 1920, h: 1080, format: 'jpg', group: ASSET_DELIVERABLE_GROUPS.PACKAGE },
  { name: 'splash_hd.jpg', label: 'Splash Screen (HD)', w: 1280, h: 720, format: 'jpg', group: ASSET_DELIVERABLE_GROUPS.PACKAGE },
  { name: 'splash_sd.jpg', label: 'Splash Screen (SD)', w: 720, h: 480, format: 'jpg', group: ASSET_DELIVERABLE_GROUPS.PACKAGE },
  { name: 'bg_fhd.jpg', label: 'App Background', w: 1920, h: 1080, format: 'jpg', group: ASSET_DELIVERABLE_GROUPS.EXPERIENCE },
  { name: 'oh_logo_fhd.png', label: 'Overhang Logo (transparent)', w: 400, h: 400, format: 'png', group: ASSET_DELIVERABLE_GROUPS.EXPERIENCE },
  { name: 'oh_bg_fhd.jpg', label: 'Overhang Background', w: 1920, h: 1080, format: 'jpg', group: ASSET_DELIVERABLE_GROUPS.EXPERIENCE },
  { name: 'store_poster.png', label: 'Store Poster', w: 540, h: 405, format: 'png', group: ASSET_DELIVERABLE_GROUPS.LISTING },
  { name: 'screenshot_1.png', label: 'Store Screenshot 1', w: 1920, h: 1080, format: 'png', group: ASSET_DELIVERABLE_GROUPS.LISTING },
  { name: 'screenshot_2.png', label: 'Store Screenshot 2', w: 1920, h: 1080, format: 'png', group: ASSET_DELIVERABLE_GROUPS.LISTING },
];

/** Per-file cap for manual deliverable uploads. */
export const ASSET_DELIVERABLE_MAX_BYTES = 10 * 1024 * 1024;
